'use client';

import { useState, useEffect, useCallback } from 'react';
import FilterPanel, { FilterState } from './FilterPanel';
import DealCard from './DealCard';
import DealDetailModal from './DealDetailModal';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface ActiveDeal {
  id: string;
  title: string;
  category: string;
  status: 'active' | 'monitoring' | 'closing';
  currentValue: string;
  targetROI: string;
  progress: number;
  image: string;
  alt: string;
  startDate: string;
  expectedCompletion: string;
  strategy: string;
  riskLevel: 'low' | 'medium' | 'high';
  highlights: string[];
  description: string;
  metrics: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
  updates: Array<{
    date: string;
    title: string;
    description: string;
  }>;
}

const ActiveDealsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<ActiveDeal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredDeals, setFilteredDeals] = useState<ActiveDeal[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const activeDeals: ActiveDeal[] = [
  {
    id: 'active-001',
    title: 'Tech Startup Series B Investment',
    category: 'Technology',
    status: 'active',
    currentValue: '$850K',
    targetROI: '+45%',
    progress: 68,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130d53363-1769451772821.png",
    alt: 'Modern tech startup office with innovative workspace design',
    startDate: 'Dec 15, 2025',
    expectedCompletion: 'Jun 15, 2026',
    strategy: 'Growth',
    riskLevel: 'medium',
    highlights: [
    'AI-powered SaaS platform with 300% YoY growth',
    'Secured partnerships with Fortune 500 companies',
    'Expanding to European markets in Q2 2026',
    'Strong leadership team with proven track record'],

    description: 'Strategic investment in a rapidly growing AI-powered SaaS platform revolutionizing enterprise workflow automation. The company has demonstrated exceptional growth metrics and is positioned for significant market expansion.',
    metrics: [
    { label: 'Current Valuation', value: '$12.5M', icon: 'CurrencyDollarIcon' },
    { label: 'Monthly Revenue', value: '$180K', icon: 'ArrowTrendingUpIcon' },
    { label: 'Customer Growth', value: '+85%', icon: 'UserGroupIcon' },
    { label: 'Market Share', value: '12%', icon: 'ChartPieIcon' }],

    updates: [
    {
      date: 'Feb 05, 2026',
      title: 'Major Partnership Announced',
      description: 'Secured strategic partnership with leading enterprise software provider, expanding market reach significantly.'
    },
    {
      date: 'Jan 28, 2026',
      title: 'Revenue Milestone Achieved',
      description: 'Surpassed $2M in annual recurring revenue, 3 months ahead of projections.'
    },
    {
      date: 'Jan 15, 2026',
      title: 'Product Launch Success',
      description: 'New AI features launched with overwhelmingly positive customer feedback and adoption.'
    }]

  },
  {
    id: 'active-002',
    title: 'Renewable Energy Infrastructure',
    category: 'Energy',
    status: 'monitoring',
    currentValue: '$1.2M',
    targetROI: '+38%',
    progress: 52,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e29e8f2f-1764854720540.png",
    alt: 'Solar panel farm with wind turbines in sustainable energy facility',
    startDate: 'Nov 20, 2025',
    expectedCompletion: 'Aug 20, 2026',
    strategy: 'Income',
    riskLevel: 'low',
    highlights: [
    'Government-backed renewable energy project',
    'Long-term power purchase agreements secured',
    '25-year guaranteed revenue stream',
    'Environmental impact: 50K tons CO2 reduction annually'],

    description: 'Investment in large-scale solar and wind energy infrastructure with guaranteed government contracts and stable long-term returns. Project contributes significantly to regional renewable energy goals.',
    metrics: [
    { label: 'Energy Capacity', value: '50 MW', icon: 'BoltIcon' },
    { label: 'Annual Revenue', value: '$2.8M', icon: 'BanknotesIcon' },
    { label: 'Contract Duration', value: '25 years', icon: 'DocumentCheckIcon' },
    { label: 'ROI Timeline', value: '9 months', icon: 'ClockIcon' }],

    updates: [
    {
      date: 'Feb 01, 2026',
      title: 'Construction Phase 2 Complete',
      description: 'Second phase of solar panel installation completed ahead of schedule, now at 52% total capacity.'
    },
    {
      date: 'Jan 10, 2026',
      title: 'Grid Connection Approved',
      description: 'Received final approval for grid connection, enabling power distribution to begin next month.'
    }]

  },
  {
    id: 'active-003',
    title: 'Healthcare Technology Expansion',
    category: 'Healthcare',
    status: 'active',
    currentValue: '$650K',
    targetROI: '+52%',
    progress: 74,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107729efc-1767173885748.png",
    alt: 'Modern healthcare facility with advanced medical technology equipment',
    startDate: 'Jan 05, 2026',
    expectedCompletion: 'May 05, 2026',
    strategy: 'Growth',
    riskLevel: 'medium',
    highlights: [
    'FDA-approved telemedicine platform',
    'Serving 50,000+ patients across 15 states',
    'Partnership with major insurance providers',
    'Expanding into mental health services'],

    description: 'Strategic investment in a leading telemedicine platform experiencing rapid adoption. The company is expanding service offerings and geographic reach while maintaining strong unit economics.',
    metrics: [
    { label: 'Active Patients', value: '50K+', icon: 'UserGroupIcon' },
    { label: 'Monthly Consultations', value: '12K', icon: 'ChatBubbleLeftRightIcon' },
    { label: 'Provider Network', value: '800+', icon: 'BuildingOffice2Icon' },
    { label: 'Patient Satisfaction', value: '4.8/5', icon: 'StarIcon' }],

    updates: [
    {
      date: 'Feb 08, 2026',
      title: 'Insurance Partnership Expansion',
      description: 'Added 3 major insurance providers to network, covering additional 2M potential patients.'
    },
    {
      date: 'Jan 25, 2026',
      title: 'Mental Health Services Launch',
      description: 'Successfully launched mental health counseling services with 150 licensed therapists.'
    }]

  },
  {
    id: 'active-004',
    title: 'Commercial Real Estate Development',
    category: 'Real Estate',
    status: 'closing',
    currentValue: '$2.1M',
    targetROI: '+28%',
    progress: 89,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d1cc887-1766601325766.png",
    alt: 'Modern commercial building under construction in urban business district',
    startDate: 'Aug 10, 2025',
    expectedCompletion: 'Mar 10, 2026',
    strategy: 'Value',
    riskLevel: 'low',
    highlights: [
    'Prime downtown location with high demand',
    '85% pre-leased to quality tenants',
    'LEED Gold certified sustainable building',
    'Expected property appreciation of 15%'],

    description: 'Development of a Class A commercial office building in a prime downtown location. Strong pre-leasing activity and sustainable design features position this for excellent returns.',
    metrics: [
    { label: 'Total Square Feet', value: '125K', icon: 'BuildingOfficeIcon' },
    { label: 'Pre-Leased', value: '85%', icon: 'DocumentCheckIcon' },
    { label: 'Avg Rent/SF', value: '$45', icon: 'CurrencyDollarIcon' },
    { label: 'Completion', value: '89%', icon: 'ChartBarIcon' }],

    updates: [
    {
      date: 'Feb 07, 2026',
      title: 'Final Tenant Signed',
      description: 'Secured final major tenant for 20K sq ft, bringing total occupancy to 85% before completion.'
    },
    {
      date: 'Jan 30, 2026',
      title: 'LEED Gold Certification Achieved',
      description: 'Building officially certified LEED Gold, enhancing property value and tenant appeal.'
    }]

  },
  {
    id: 'active-005',
    title: 'E-commerce Platform Acquisition',
    category: 'Technology',
    status: 'active',
    currentValue: '$950K',
    targetROI: '+41%',
    progress: 61,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1520cf999-1764675863237.png",
    alt: 'Modern e-commerce warehouse with automated fulfillment systems',
    startDate: 'Dec 01, 2025',
    expectedCompletion: 'Jul 01, 2026',
    strategy: 'Balanced',
    riskLevel: 'medium',
    highlights: [
    'Established brand with loyal customer base',
    'Proprietary technology platform',
    'Expanding into international markets',
    'Strong cash flow and profitability'],

    description: 'Acquisition and growth investment in a profitable e-commerce platform with strong brand recognition. Focus on international expansion and technology enhancement to drive growth.',
    metrics: [
    { label: 'Monthly GMV', value: '$1.2M', icon: 'ShoppingCartIcon' },
    { label: 'Active Customers', value: '45K', icon: 'UserGroupIcon' },
    { label: 'Repeat Rate', value: '68%', icon: 'ArrowPathIcon' },
    { label: 'Profit Margin', value: '22%', icon: 'ChartPieIcon' }],

    updates: [
    {
      date: 'Feb 06, 2026',
      title: 'International Launch Success',
      description: 'Successfully launched in 3 European markets with strong initial sales performance.'
    },
    {
      date: 'Jan 20, 2026',
      title: 'Technology Platform Upgrade',
      description: 'Completed major platform upgrade improving site speed by 40% and conversion rate by 15%.'
    }]

  },
  {
    id: 'active-006',
    title: 'Sustainable Agriculture Fund',
    category: 'Agriculture',
    status: 'monitoring',
    currentValue: '$780K',
    targetROI: '+35%',
    progress: 58,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18dfa5d2b-1767554752590.png",
    alt: 'Modern sustainable farm with advanced irrigation and greenhouse systems',
    startDate: 'Oct 15, 2025',
    expectedCompletion: 'Sep 15, 2026',
    strategy: 'Income',
    riskLevel: 'low',
    highlights: [
    'Organic certification and premium pricing',
    'Direct-to-consumer distribution channels',
    'Water-efficient irrigation technology',
    'Long-term supply contracts with retailers'],

    description: 'Investment in sustainable agriculture operations utilizing advanced farming techniques and direct distribution. Strong demand for organic produce drives consistent returns.',
    metrics: [
    { label: 'Farm Acreage', value: '500 acres', icon: 'MapIcon' },
    { label: 'Annual Yield', value: '$2.5M', icon: 'CurrencyDollarIcon' },
    { label: 'Retail Partners', value: '25', icon: 'BuildingStorefrontIcon' },
    { label: 'Water Savings', value: '40%', icon: 'BeakerIcon' }],

    updates: [
    {
      date: 'Feb 03, 2026',
      title: 'Harvest Season Success',
      description: 'Winter harvest exceeded projections by 18%, with premium pricing maintained.'
    },
    {
      date: 'Jan 18, 2026',
      title: 'New Retail Partnership',
      description: 'Secured distribution agreement with major organic grocery chain for 50 locations.'
    }]

  }];


  const handleFilterChange = useCallback((filters: FilterState) => {
    let filtered = [...activeDeals];

    if (filters.category !== 'all') {
      filtered = filtered.filter((deal) => deal.category.toLowerCase() === filters.category);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter((deal) => deal.status === filters.status);
    }

    if (filters.strategy !== 'all') {
      filtered = filtered.filter((deal) => deal.strategy.toLowerCase() === filters.strategy);
    }

    if (filters.sortBy === 'progress-high') {
      filtered.sort((a, b) => b.progress - a.progress);
    } else if (filters.sortBy === 'progress-low') {
      filtered.sort((a, b) => a.progress - b.progress);
    } else if (filters.sortBy === 'value-high') {
      filtered.sort((a, b) => {
        const aValue = parseFloat(a.currentValue.replace(/[$MK,]/g, ''));
        const bValue = parseFloat(b.currentValue.replace(/[$MK,]/g, ''));
        return bValue - aValue;
      });
    }

    setFilteredDeals(filtered);
  }, []);

  const handleDealClick = (deal: ActiveDeal) => {
    setSelectedDeal(deal);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedDeal(null), 300);
  };

  if (!isHydrated) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse"></div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="h-96 bg-card rounded-lg animate-pulse"></div>
          </div>
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) =>
              <div key={i} className="h-80 bg-card rounded-lg animate-pulse"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        <div className="lg:col-span-3">
          {filteredDeals.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredDeals.map((deal) =>
            <DealCard key={deal.id} deal={deal} onClick={() => handleDealClick(deal)} />
            )}
            </div> :

          <div className="bg-card rounded-lg shadow-card p-12 text-center border border-border">
              <Icon name="PhoneIcon" size={48} variant="outline" className="mx-auto text-lime-600 mb-4" />
              <h3 className="text-xl font-heading font-semibold text-foreground mb-6">Need Assistance?</h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="tel:+12018346089" className="px-6 py-3 bg-lime-600 text-white rounded-lg hover:bg-lime-700 transition-colors font-medium flex items-center gap-2">
                  <Icon name="PhoneIcon" size={20} variant="solid" />
                  Contact Information: +12018346089
                </a>
                <Link href="/about-our-track-record" className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium flex items-center gap-2">
                  <Icon name="ExclamationTriangleIcon" size={20} variant="solid" />
                  Report Problems
                </Link>
              </div>
            </div>
          }
        </div>
      </div>

      {selectedDeal &&
      <DealDetailModal deal={selectedDeal} isOpen={isModalOpen} onClose={handleCloseModal} />
      }
    </div>);

};

export default ActiveDealsSection;